import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import UISectionWrapper, {
  UISectionHeader,
  UISectionBody,
} from '@/components/shared/UISectionWrapper';
import UISelect from '@/components/shared/UISelect';
import UISimpleTable, {
  SimpleTableColumn,
} from '@/components/shared/UISimpleTable';
import UIBadge, { type BadgeVariant } from '@/components/shared/UIBadge';
import UIButton from '@/components/shared/UIButton';
import { useNotifications } from '@/hooks/useNotifications';
import { Notification } from '@/types/notifications';
import useGetDictionaries from '@/hooks/useGetDictionaries';
import './NotificationsList.scss';
import UIPaginator from '@/components/shared/UIPaginator';
import { profileSelector } from '@/store/profile/profileSlices';
import UIProperty from '@/components/shared/UIProperty';
import { useToast } from '@/components/shared/ToastProvider';
import { safeToLowerCase } from '@/utils/stringUtils';

interface NotificationsListProps {
  selectedType: string;
  onTypeChange: (type: string) => void;
}

interface NotificationTypeDictionary {
  notificationEntityTypes: string[];
}

const getBadgeVariantFromEntityType = (entityType: string): BadgeVariant => {
  switch (safeToLowerCase(entityType)) {
    case 'payment':
      return 'primary';
    case 'ticket':
      return 'warning';
    case 'task':
      return 'info';
    case 'lease':
      return 'success';
    default:
      return 'secondary';
  }
};

const NotificationsList: React.FC<NotificationsListProps> = ({
  selectedType,
  onTypeChange,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [notificationsData, setNotificationsData] = useState<Notification[]>([]);
  const [totalNotifications, setTotalNotifications] = useState(0);
  const { showSuccess, showError } = useToast();

  const profile = useSelector(profileSelector);
  const itemsPerPage = 10;
  const { fetchNotifications, markAllAsRead, loading, error } = useNotifications();
  const {
    data: dictionaries,
    isLoading: isDictionariesLoading,
    error: dictionariesError,
  } = useGetDictionaries<NotificationTypeDictionary>('notifications');

  const typeOptions = useMemo(() => {
    if (!dictionaries?.notificationEntityTypes) return [{ value: 'all-types', label: 'All Types' }];
    return [
      { value: 'all-types', label: 'All Types' },
      ...dictionaries.notificationEntityTypes.map((type: string) => ({
        value: type,
        label: type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      })),
    ];
  }, [dictionaries?.notificationEntityTypes]);

  const loadNotifications = useCallback(async () => {
    if (!profile?.id) return;

    const filters = {
      tenantId: profile.id,
      type: selectedType,
      limit: itemsPerPage,
      page: currentPage,
    };

    const data = await fetchNotifications(filters);
    if (data) {
      setNotificationsData(data.notifications || []);
      setTotalNotifications(data.total || 0);
    }
  }, [currentPage, fetchNotifications, profile?.id, selectedType, itemsPerPage]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkAllAsRead = async () => {
    const success = await markAllAsRead();
    if (success) {
      showSuccess('All notifications marked as read.');
      loadNotifications();
    } else {
      showError('Failed to mark notifications as read.');
    }
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    onTypeChange(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const totalItems = totalNotifications;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const pageInfo = `${(currentPage - 1) * itemsPerPage + 1}-${Math.min(
    currentPage * itemsPerPage,
    totalItems
  )} of ${totalItems}`;

  const columns: SimpleTableColumn[] = [
    {
      key: 'createdAt',
      label: 'DATE',
      render: (value, row: Notification) => <span>{new Date(row.createdAt).toLocaleDateString()}</span>,
    },
    {
      key: 'entityType',
      label: 'CATEGORY',
      render: (value, row: Notification) => (
        <UIBadge variant={getBadgeVariantFromEntityType(row.entityType)} shape="pill">{row.entityType.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())}</UIBadge>
      ),
    },
    {
      key: 'title',
      label: 'SUBJECT',
      render: (value, row: Notification) => <span>{row.title}</span>,
    },
    {
      key: 'message',
      label: 'MESSAGE',
      render: (value, row: Notification) => <span>{row.message}</span>,
    },
    {
      key: 'property',
      label: 'PROPERTY',
      render: (value, row: Notification) => {
        if (row.lease) {
          return <UIProperty name={row.lease.unit.property.name} type={row.lease.unit.property.type as 'residential' | 'commercial' | 'mixed'} />;
        }
        return null;
      },
    },
    {
      key: 'unit',
      label: 'UNIT',
      render: (value, row: Notification) => {
          if (row.lease) {
              return <span>{row.lease.unit.number}</span>;
          }
          return null;
      }
  },
    {
      key: 'isRead',
      label: 'STATUS',
      render: (value, row: Notification) => {
        return row.isRead ? (
          <UIBadge variant="success" shape="rounded">READ</UIBadge>
        ) : (
          <UIBadge variant="primary" shape="rounded">UNREAD</UIBadge>
        );
      },
    },

  ];

  const isDataLoading = isDictionariesLoading || loading;

  return (
    <UISectionWrapper spacing="default" align="stretch" shadow="default">
      <UISectionHeader>
        <div className="NotificationsList__header">
          <div className="NotificationsList__title-row">
            <div className="NotificationsList__title-content">
              <h2 className="NotificationsList__title">All Notifications</h2>
              <UISelect
                size="medium"
                value={selectedType}
                onChange={handleTypeChange}
                options={typeOptions}
                className="NotificationsList__type-select"
                disabled={isDataLoading}
              />
            </div>
            <UIButton
              variant="primary"
              size="medium"
              onClick={handleMarkAllAsRead}
              disabled={isDataLoading}
            >
              Mark all as read
            </UIButton>
          </div>
          <div className="NotificationsList__separator"></div>
        </div>
      </UISectionHeader>

      <UISectionBody>
        <div className="NotificationsList__content">
          {isDataLoading && (
            <div className="NotificationsList__loading">Loading...</div>
          )}
          {(error || dictionariesError) && (
            <div className="NotificationsList__error">Error loading data.</div>
          )}
          {!isDataLoading && !error && !dictionariesError && (
            <>
              <UISimpleTable
                data={notificationsData}
                columns={columns}
                className="NotificationsList__table"
                getRowKey={(row) => row.id}
              />
              {totalPages > 1 && (
                <div className="NotificationsList__pagination">
                  <div className="NotificationsList__page-info">{pageInfo}</div>
                  <UIPaginator
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    className="NotificationsList__paginator"
                  />
                </div>
              )}
              {notificationsData.length === 0 && !isDataLoading && (
                <div className="NotificationsList__no-data">No notifications found.</div>
              )}
            </>
          )}
        </div>
      </UISectionBody>
    </UISectionWrapper>
  );
};

export default NotificationsList;
